import React, { useEffect, useMemo, useRef } from "react";
import GenericCard from "@ui-components/Card/GenericCard";
import { GenericTag } from "@ui-components/Tag/GenericTag";
import { GenericTable, GenericTableColumn } from "@ui-components/Table/GenericTable";
import { DETAIL_TABLE_MAX_HEIGHT } from "../detailTable";
import SegmentedControl from "@ui-components/SegmentedContol/GenericSegmentedContol";
import { SkeletonBlock } from "@ui-components/Skeleton/GenericSkeleton";
import { EMPTY, NO_READING, timeAgo } from "../overview/overviewFormat";
import CardHeading from "../overview/CardHeading";
import { isNeverUsed, isToReview, type FirewallPolicies, type FirewallPolicy, type PolicyFilter } from "./policies.types";
import type { Reading } from "../readings/useFwaasReading";
import { scrollBehavior } from "../detailScroll";

type PoliciesReading = Reading<FirewallPolicies>;

const HIDDEN = "Hidden address";

/** A name the backend withheld (it carried an address) reads as hidden, never as blank. */
function Names({ names }: { names: (string | null)[] }) {
    if (names.length === 0) return <>{EMPTY}</>;
    return (
        <>
            {names.map((name, i) => (
                <React.Fragment key={`${name ?? HIDDEN}-${i}`}>
                    {i > 0 ? ", " : null}
                    {name ?? (
                        <span className="italic text-semantic-text-secondary" title="Hidden: the name contains an address">
                            {HIDDEN}
                        </span>
                    )}
                </React.Fragment>
            ))}
        </>
    );
}

const compactCount = new Intl.NumberFormat("en-US", { notation: "compact", maximumFractionDigits: 1 });

/** Disabled rules stay in their place — order matters — but step back visually. */
const muted = (policy: FirewallPolicy, node: React.ReactNode) => (
    <span className={policy.enabled ? undefined : "text-semantic-text-secondary"}>{node}</span>
);

/** The rule's name, and under it the interfaces it joins — one idea, one cell. */
function Rule({ policy }: { policy: FirewallPolicy }) {
    return muted(
        policy,
        <span className="flex flex-col gap-0.5">
            <span className="inline-flex flex-wrap items-center gap-2 font-medium">
                {policy.name ?? <span className="italic">Hidden name</span>}
                {!policy.enabled && <GenericTag label="Off" variant="neutral" size="small" />}
            </span>
            <span className="text-xs text-semantic-text-secondary">
                <Names names={policy.from} /> → <Names names={policy.to} />
            </span>
        </span>
    );
}

/** Source → destination on one line; long names wrap inside the column, never into the next. */
function Addresses({ policy }: { policy: FirewallPolicy }) {
    return muted(
        policy,
        <>
            <Names names={policy.sources} /> <span className="text-semantic-text-secondary">→</span>{" "}
            <Names names={policy.destinations} />
        </>
    );
}

/**
 * Hits and last use answer one question — is this rule doing anything? A disabled rule
 * has no counters; an enabled one at 0 is the finding worth a tag. Bytes were dropped:
 * next to hits they added a second large number and no new answer.
 */
export function RuleUsage({ policy }: { policy: FirewallPolicy }) {
    if (!policy.enabled) return <span className="text-semantic-text-secondary">{EMPTY}</span>;
    if (isNeverUsed(policy)) return <GenericTag label="Never used" variant="warning" size="small" />;
    return (
        <span className="flex flex-col gap-0.5">
            <span className="tabular-nums" title={policy.hits?.toLocaleString("en-US")}>
                {policy.hits == null ? EMPTY : `${compactCount.format(policy.hits)} hits`}
            </span>
            {policy.last_used_at ? (
                <span className="text-xs text-semantic-text-secondary">Last {timeAgo(policy.last_used_at)}</span>
            ) : null}
        </span>
    );
}

const COLUMNS: GenericTableColumn<FirewallPolicy>[] = [
    // `data-rule-id` lets the page find a rule's row to show it (CTX-1265).
    { header: "#", width: "48px", renderCell: (p) => <span data-rule-id={p.id}>{muted(p, p.id)}</span> },
    { header: "Rule", width: "28%", wrapMode: "break", renderCell: (p) => <Rule policy={p} /> },
    { header: "Source → Destination", width: "32%", wrapMode: "break", renderCell: (p) => <Addresses policy={p} /> },
    { header: "Services", width: "22%", wrapMode: "wrap", renderCell: (p) => muted(p, <Names names={p.services} />) },
    { header: "Usage", width: "120px", renderCell: (p) => <RuleUsage policy={p} /> },
];

const ROWS_PER_PAGE = 50;

interface FilterProps {
    filter: PolicyFilter;
    onFilterChange: (filter: PolicyFilter) => void;
}

function PoliciesTable({ policies, filter, onFilterChange }: { policies: FirewallPolicies } & FilterProps) {
    const toReview = useMemo(() => policies.items.filter(isToReview), [policies]);
    const rows = filter === "to-review" ? toReview : policies.items;

    if (policies.items.length === 0) {
        return <p className="text-sm text-semantic-text-secondary">No rules configured.</p>;
    }
    return (
        <div className="flex flex-col gap-3">
            <SegmentedControl
                options={[
                    { value: "all", label: `All (${policies.items.length})` },
                    { value: "to-review", label: `To review (${toReview.length})` },
                ]}
                value={filter}
                onChange={(value) => onFilterChange(value as PolicyFilter)}
                size="small"
                maxWidthClass="max-w-[320px]"
            />
            <GenericTable
                maxTableBodyHeight={DETAIL_TABLE_MAX_HEIGHT}
                columns={COLUMNS}
                data={rows}
                rowKey={(p) => p.id}
                width="100%"
                initialRowsPerPage={ROWS_PER_PAGE}
                // The table scrolls inside itself; a pager only earns its room past one page.
                isFooterVisible={rows.length > ROWS_PER_PAGE}
                tableClassName="bg-semantic-bg-primary"
            />
        </div>
    );
}

function PoliciesBody({ policies, ...filterProps }: { policies: PoliciesReading } & FilterProps) {
    if (policies === undefined) {
        return (
            <div className="flex flex-col gap-3" aria-busy>
                <SkeletonBlock h={32} w={240} className="rounded" />
                <SkeletonBlock h={20} w="100%" className="rounded" />
                <SkeletonBlock h={20} w="100%" className="rounded" />
                <SkeletonBlock h={20} w="100%" className="rounded" />
            </div>
        );
    }
    if (policies === null) return <p className="text-sm text-semantic-text-secondary">{NO_READING}</p>;
    return <PoliciesTable policies={policies} {...filterProps} />;
}

/**
 * The firewall's rules (CTX-1164), in the order it evaluates them — the first match
 * wins, so the order is the information and the table is not re-sorted. Counters run
 * since the firewall last reset them, usually its last restart.
 *
 * The filter is the page's (CTX-1208): "Show rules" in Observations sets it and
 * scrolls here, to `POLICIES_ANCHOR`.
 */
export const POLICIES_ANCHOR = "fwaas-policies";

/** A rule to show, from a rule's name elsewhere on the page; `nonce` lets the same rule flash twice. */
export interface RuleFlash {
    ruleId: number;
    nonce: number;
}

const FLASH_CLASSES = ["!bg-semantic-bg-brand-secondary", "motion-safe:transition-colors", "duration-700"];
const FLASH_MS = 1600;

/**
 * Brings a rule's row into view and lights it for a moment. Done on the DOM because the
 * shared table takes no per-row class; the row is found by the `#` cell's `data-rule-id`.
 */
function useRuleFlash(container: React.RefObject<HTMLElement>, flash: RuleFlash | null) {
    useEffect(() => {
        if (!flash) return;
        const row = container.current?.querySelector(`[data-rule-id="${flash.ruleId}"]`)?.closest("tr");
        if (!row) return;
        row.scrollIntoView?.({ behavior: scrollBehavior(), block: "center" });
        const cells = Array.from(row.querySelectorAll("td"));
        cells.forEach((cell) => cell.classList.add(...FLASH_CLASSES));
        const timer = window.setTimeout(() => cells.forEach((cell) => cell.classList.remove(FLASH_CLASSES[0])), FLASH_MS);
        return () => {
            window.clearTimeout(timer);
            cells.forEach((cell) => cell.classList.remove(...FLASH_CLASSES));
        };
    }, [container, flash]);
}

export default function PoliciesCard({
    policies,
    flash = null,
    ...filterProps
}: { policies: PoliciesReading; flash?: RuleFlash | null } & FilterProps) {
    const sectionRef = useRef<HTMLElement>(null);
    useRuleFlash(sectionRef, flash);
    return (
        // Fills its grid row, so it stands as tall as Licenses beside it (CTX-1272).
        <GenericCard isCollapsible={false} variant="primary" shadow="sm" className="h-full">
            <section ref={sectionRef} id={POLICIES_ANCHOR} className="p-5 scroll-mt-4" aria-label="Policies" aria-busy={policies === undefined}>
                <CardHeading
                    className="mb-3"
                    title="Policies"
                    subtitle="In the order the firewall applies them · usage since its last restart"
                />
                <PoliciesBody policies={policies} {...filterProps} />
            </section>
        </GenericCard>
    );
}
